import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { RequestByIdApiType } from '@/api/models/requests'
import { useRequestTypes } from '@/api/models/requests/hooks/useRequestTypes'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { PassAutosFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassAutosFormBlock'
import { PassFormHeader } from '@/components/entities/passes/ui/PassForms/FormBlock/PassFormHeader'
import { PassParkingPlug } from '@/components/entities/passes/ui/PassParkingPlug'
import { RequestDatePickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestDatePickerFormBlock'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import { useFormDirty } from '@/components/entities/requests/ui/hooks/useFormDirty'
import {
  formatAutosData,
  getDefaultAutos,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'

import { useParkingPlaces } from '../../../requests/hooks/useParkingPlaces'
import {
  AutosApiData,
  AutosFormData,
  AutosFormErrors,
  generateAutoPassFormSchema
} from '../../../requests/ui/model/schemas'
import { RentParkingPlug } from './RentPassAutoForm/RentParkingPlug'

type Props = {
  onFormDirty: (_: boolean) => void
  pass?: RequestByIdApiType
  onSubmit: (data: AutosApiData) => Promise<void>
  passError?: AxiosError<ServerValidationError> | null
  isPending?: boolean
}

const GuestPassAutoForm: FC<Props> = ({
  onFormDirty,
  pass,
  onSubmit,
  passError,
  isPending
}) => {
  const { rooms } = useSelectedProjectPremises()
  const { data: requestTypes } = useRequestTypes()

  const { parkingPlaces } = useParkingPlaces()

  const isAutoRentingAvailable = requestTypes?.some(
    type => type.alias === RequestTypeAlias.RentPassAuto
  )

  const form = useForm<AutosFormData, AutosFormErrors>({
    defaultValues: {
      room: undefined,
      autos: [
        {
          number: '',
          model: '',
          parkingPlace: parkingPlaces[0],
          isStandard: true
        }
      ]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateAutoPassFormSchema())
  })

  const { formState, handleSubmit, reset } = form

  useFormDirty(onFormDirty, formState)

  useEffect(() => {
    reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      autos: getDefaultAutos(pass, parkingPlaces)
    })
  }, [parkingPlaces, pass, reset, rooms])

  const handleFormSubmit = async (data: AutosFormData) => {
    await onSubmit(formatAutosData(data))
  }

  if (parkingPlaces.length === 0 && isAutoRentingAvailable) {
    return <RentParkingPlug />
  }

  if (parkingPlaces.length === 0 && !isAutoRentingAvailable) {
    return <PassParkingPlug />
  }

  return (
    <DrawerFormLayout
      onSubmit={handleSubmit(handleFormSubmit)}
      isPending={isPending}
      header={
        <PassFormHeader text="Временный пропуск на автомобиль даёт право на въезд на территорию один раз" />
      }
    >
      <div className="form-block-cover mb-16 grid grid-cols-2 gap-24 p-32 lg:grid-cols-1 lg:gap-16 lg:p-16">
        <FormProvider {...form}>
          <RequestDatePickerFormBlock passError={passError} />
        </FormProvider>
        <FormProvider {...form}>
          <RequestRoomPickerFormBlock />
        </FormProvider>
      </div>
      <FormProvider {...form}>
        <PassAutosFormBlock
          typeAlias={RequestTypeAlias.GuestPassAuto}
          passError={passError}
        />
      </FormProvider>
    </DrawerFormLayout>
  )
}

export { GuestPassAutoForm }
